var __defProp = Object.defineProperty;
var __defNormalProp = (obj, key, value) => key in obj ? __defProp(obj, key, { enumerable: true, configurable: true, writable: true, value }) : obj[key] = value;
var __publicField = (obj, key, value) => {
  __defNormalProp(obj, typeof key !== "symbol" ? key + "" : key, value);
  return value;
};
import isEqual from "lodash.isequal";
import MicroEmitter from "micro-emitter";
import { DEFAULT_ROW_HEIGHT, OVERSCAN_ROW_COUNT } from "../../components/Table/constants.js";
import { Debuggable } from "../debug.js";
import { groupByRow } from "../groupByRow.js";
import { clone } from "../utils.js";
import { FetchStatus, EScrollDirection } from "./types.js";
const MAX_CELL_HISTORY = 10;
const MAX_BUFFER_HISTORY = 3;
const DEFAULT_PAGE_SIZE = 100;
const GET_ROWS_LIMIT = 25e3;
const DEFAULT_BUFFER_ID = "buffer";
const lowestToHighest = (a, b) => a - b;
const emitter = new MicroEmitter();
class TableDataBuffer extends Debuggable {
  constructor() {
    super();
    /**
     * The id of the current buffer. Allows us to store up to three buffer states
     * as well as to ensure that updates are always associated with the correct
     * set of data.
     */
    __publicField(this, "_bufferId", DEFAULT_BUFFER_ID);
    /**
     * Stores the loading status of pages as they are fetched.
     */
    __publicField(this, "_pageCache", /* @__PURE__ */ new Map());
    /**
     * Stores maximum number of pages of data that can be stored in the cache.
     */
    __publicField(this, "_pageCacheLimit", 10);
    /**
     * The number of records fetched per page.
     */
    __publicField(this, "_pageSize", DEFAULT_PAGE_SIZE);
    /**
     * Stores the pages that are currently scrolled in view.
     */
    __publicField(this, "_pagePointers", [0]);
    /**
     * The latest version of data in the cache, used to catch race conditions
     */
    __publicField(this, "_latestVersion", 0);
    /**
     * Records currently stored in the buffer. Each record collection is keyed
     * by its associated bufferId.
     */
    __publicField(this, "_recordsCache", /* @__PURE__ */ new Map());
    /**
     * The total size of the data associated with a bufferId.
     */
    __publicField(this, "_totalRecordsCache", /* @__PURE__ */ new Map());
    /**
     * Stores cells that are currently in the process of updating.
     * This way changes to the underlying data will not affect the value
     * returned for that cell.
     */
    __publicField(this, "_lockedCells", /* @__PURE__ */ new Map());
    /**
     * If this param is true, then rows will remain locked and uneditable while
     * being updated.
     */
    __publicField(this, "_lockActiveRecords", false);
    /**
     * Stores rows that are currently in the process of updating. This allows us
     * to control the row data while updating if desired.
     */
    __publicField(this, "_lockedRecords", /* @__PURE__ */ new Map());
    /**
     * The history of cell edits associated with a bufferId. We store history to
     * a maximum of ten places.
     */
    __publicField(this, "_cellHistoryCache", /* @__PURE__ */ new Map());
    /**
     * The column config associated with the current buffer.
     */
    __publicField(this, "_columnConfig");
    /**
     * The search request params associated with the current buffer.
     */
    __publicField(this, "_params", {});
    /**
     * The cache of cell update listeners. We store them so that they can be
     * garbage collected as needed. The cache should never be larger than the
     * number of cells currently rendered.
     */
    __publicField(this, "listeners", /* @__PURE__ */ new Map());
    /**
     * The function that loads more data (GET).
     */
    __publicField(this, "onDataLoad");
    /**
     * The function that updates the data (POST/PUT).
     */
    __publicField(this, "onDataUpdate");
    /**
     * Callbacks to interact with the data load/update requests.
     */
    __publicField(this, "onDataLoadStart");
    __publicField(this, "onDataLoadSuccess");
    __publicField(this, "onDataLoadError");
    __publicField(this, "onDataUpdateSuccess");
    __publicField(this, "onDataAddSuccess");
    __publicField(this, "onDataUpdateError");
    /**
     * The height of a row in the table.
     */
    __publicField(this, "rowHeight", DEFAULT_ROW_HEIGHT);
    __publicField(this, "injectNewRecordRow", (insertAfter) => {
      const records = this.bufferedRecords;
      const referenceRecord = this.bufferedRecords.find(
        (r) => r.id === insertAfter
      );
      if (!referenceRecord) {
        return [];
      }
      const recordsToPatch = records.slice(referenceRecord.position + 1);
      const patchedRecords = recordsToPatch.map((r) => {
        r.position++;
        r.rowIndex++;
        return r;
      });
      this.updateBufferedRecord(referenceRecord.position + 1, {
        cells: [{ column: 0, value: "" }],
        position: referenceRecord.position + 1,
        rowIndex: referenceRecord.rowIndex + 2
      });
      patchedRecords.forEach((r) => {
        this.updateBufferedRecord(r.position, r);
      });
    });
  }
  /**
   * Sets the current bufferId and garbage collects old buffers if at the limit.
   *
   * @param id
   */
  setBufferId(id) {
    this.$debugMethod("setBufferId");
    const bufferKeys = Array.from(this._recordsCache.keys());
    const bufferId = id ?? DEFAULT_BUFFER_ID;
    if (bufferKeys.length >= MAX_BUFFER_HISTORY && !this._recordsCache.get(bufferId)) {
      const key = bufferKeys[0];
      this._recordsCache.delete(key);
      this._pageCache.delete(key);
      this._totalRecordsCache.delete(key);
      this._cellHistoryCache.delete(key);
    }
    this._bufferId = bufferId;
  }
  /**
   * Attempts to set a new version. The new version is rejected if it is lower
   * than the current version.
   *
   * @param version
   * @param key
   */
  setVersion(version = 0, key) {
    this.$debugMethod("setVersion");
    if (version >= this._latestVersion) {
      this._latestVersion = version;
      return true;
    }
    this.$warn(
      key ?? this._bufferId,
      `attempted to set version ${version}, which is earlier than the current version ${this._latestVersion}`
    );
    return false;
  }
  /**
   * Configures the data loading callbacks, and sets them to undefined by default
   * if no option is provided.
   *
   * @param options
   */
  setDataLoadCallbacks(options) {
    this.onDataLoadStart = options == null ? void 0 : options.onDataLoadStart;
    this.onDataLoadSuccess = options == null ? void 0 : options.onDataLoadSuccess;
    this.onDataLoadError = options == null ? void 0 : options.onDataLoadError;
  }
  /**
   * Sets a total records count for the current buffer.
   *
   * @param total
   */
  setTotalRecords(total) {
    this._totalRecordsCache.set(this._bufferId, total ?? 0);
  }
  /**
   * Performs the initial configuration for the current buffer.
   *
   * @param options
   */
  initialize(options) {
    this.$debugMethod("initialize");
    this.setBufferId(options.bufferId);
    this._pageSize = options.pageSize ?? DEFAULT_PAGE_SIZE;
    this._params = options.params ?? {};
    this._columnConfig = options.columnConfig;
    this._latestVersion = options.version ?? 0;
    this.setTotalRecords(options.totalRecords);
    this.onDataUpdateSuccess = options.onDataUpdateSuccess;
    this.onDataAddSuccess = options.onDataAddSuccess;
    this.onDataUpdateError = options.onDataUpdateError;
  }
  /**
   * Builds a key for a row.
   *
   * @param row
   * @param includeBufferId
   */
  rowKey(row, includeBufferId) {
    const key = `${row}`;
    return includeBufferId ? `${this._bufferId}:${key}` : key;
  }
  /**
   * Builds a key for a cell.
   *
   * @param row
   * @param col
   * @param includeBufferId
   */
  cellKey(row, col, includeBufferId) {
    return `${this.rowKey(row, includeBufferId)}:${col}`;
  }
  /**
   * Builds a key for a locked row. Locked row keys are based on rowId rather
   * than position so that they remain locked even if the row's position changes.
   *
   * @param row
   * @param id
   */
  lockedRowKey(row, id) {
    return id ? `${id}:${row}` : `ghost-${row}:${row}`;
  }
  /**
   * Builds a key for a locked cell. Locked cell keys are based on rowId rather
   * than position so that they remain locked even if the row's position changes.
   *
   * @param row
   * @param col
   * @param id
   */
  lockedCellKey(row, col, id) {
    return `${this.lockedRowKey(row, id)}:${col}`;
  }
  /** Extracts the row and column from a cell key
   *
   * @param key
   */
  getCellCoordinatesFromKey(key) {
    const [row, col] = key.split(":").slice(-2).map(Number);
    return { row, col };
  }
  /**
   * Builds a key for a range of rows.
   *
   * @param records
   */
  rowRangeKey(records) {
    if (!records.length) {
      return `${this._bufferId}:empty_row_array`;
    }
    const pos = records[0].position;
    return `${this._bufferId}:${pos}-${pos + records.length - 1}`;
  }
  /**
   * Emits a change for all cells in a row.
   *
   * @param row
   */
  emitRowChange(row) {
    var _a;
    this.$debugMethod("emitRowChange");
    this.emitCellChange(row, -1);
    (_a = this._columnConfig) == null ? void 0 : _a.forEach((column, col) => {
      this.emitCellChange(row, col);
    });
  }
  /**
   * Emits a change for an individual cell.
   *
   * @param row
   * @param col
   */
  emitCellChange(row, col) {
    this.$debugMethod("emitCellChange");
    const CELL_KEY = this.cellKey(row, col);
    emitter.emit(CELL_KEY, {});
  }
  /**
   * Attaches cell change listeners to the emitter. Cells are subscribed to
   * both row events and cell events. Keys for the emitter are not tied to an
   * individual buffer, as we want cells to update when the buffer changes.
   *
   * @param row
   * @param col
   * @param listener
   */
  addCellChangeListener(row, col, listener) {
    this.$debugMethod("addCellChangeListener");
    const CELL_KEY = this.cellKey(row, col);
    const cellListener = () => {
      this.$debug(CELL_KEY, "cell change listener firing");
      listener(this.getCell(row, col), this.getRow(row));
    };
    this.listeners.set(CELL_KEY, cellListener);
    emitter.addListener(CELL_KEY, cellListener);
  }
  /**
   * Removes cell change listeners from the emitter.
   *
   * @param row
   * @param col
   */
  removeCellChangeListener(row, col) {
    this.$debugMethod("removeCellChangeListener");
    const CELL_KEY = this.cellKey(row, col);
    const cellListener = this.listeners.get(CELL_KEY);
    if (cellListener) {
      emitter.removeListener(CELL_KEY, cellListener);
      this.listeners.delete(CELL_KEY);
    }
  }
  /**
   * Returns the current buffer's cache of records.
   */
  get buffer() {
    this.$debugMethod("buffer");
    return this._recordsCache.get(this._bufferId) ?? /* @__PURE__ */ new Map();
  }
  /**
   * Returns the current buffer's cache of records as an array.
   */
  get bufferedRecords() {
    this.$debugMethod("bufferedRecords");
    return Array.from(this.buffer.values() ?? []);
  }
  /**
   * Returns a record from the buffer for a given position.
   *
   * @param position
   */
  getBufferedRecord(position) {
    return this.buffer.get(position) ?? {};
  }
  /**
   * Updates a record in the buffer and emits a change event for that record.
   *
   * @param position
   * @param record
   */
  updateBufferedRecord(position, record) {
    const buffer = this.buffer;
    buffer.set(position, record);
    this._recordsCache.set(this._bufferId, buffer);
    this.emitRowChange(position);
  }
  /**
   * Deletes a record from the buffer and emits a change event for that record.
   *
   * @param position
   */
  deleteBufferedRecord(position) {
    const buffer = this.buffer;
    buffer.delete(position);
    this._recordsCache.set(this._bufferId, buffer);
    this.emitRowChange(position);
  }
  /**
   * Returns records from the buffer for a given page. Useful for fallback scenarios
   * when something fails/is cancelled and we need to fallback to the buffer state
   *
   * @param page
   */
  getRecordsFromBuffer(page) {
    let recordsAvailable = true;
    let index = 0;
    let records = [];
    while (recordsAvailable) {
      const position = page * this._pageSize + index;
      const value = this.buffer.get(position);
      if (value) {
        records.push(value);
      } else {
        recordsAvailable = false;
      }
      index++;
    }
    return records;
  }
  /**
   * Returns the total size of the data for the current buffer.
   */
  get totalRecords() {
    return this._totalRecordsCache.get(this._bufferId) ?? 0;
  }
  /**
   * Returns the cache of page loading states for the current buffer.
   */
  get pageCache() {
    return this._pageCache.get(this._bufferId) ?? /* @__PURE__ */ new Map();
  }
  /**
   * Sets a page loading state for the current buffer.
   *
   * @param page
   * @param status
   */
  setPageCache(page, status) {
    const pageCache = this.pageCache;
    pageCache.set(page, status);
    this._pageCache.set(this._bufferId, pageCache);
  }
  /**
   * Deletes a page loading state from the current buffer.
   *
   * @param page
   * @param bufferId
   */
  deletePageFromCache(page, bufferId) {
    const id = bufferId ?? this._bufferId;
    const pageCache = this._pageCache.get(id);
    if (pageCache) {
      pageCache.delete(page);
      this._pageCache.set(id, pageCache);
    }
  }
  /**
   * Calculates the last page number that can be loaded for the current buffer.
   */
  get lastPage() {
    if (!this.totalRecords || !this._pageSize) {
      return 0;
    }
    return Math.ceil(this.totalRecords / this._pageSize) - 1;
  }
  /**
   * Returns an array of pages currently loaded or loading.
   */
  get orderedPageKeys() {
    return Array.from(this.pageCache.keys()).sort(lowestToHighest);
  }
  /**
   * Returns the page furthest from the current page.
   */
  get furthestPage() {
    const orderedKeys = this.orderedPageKeys;
    const firstPage = orderedKeys[0];
    const lastPage = orderedKeys[this.pageCache.size - 1];
    const currentPage = this._pagePointers[0] ?? 0;
    if (Math.abs(lastPage - currentPage) > Math.abs(currentPage - firstPage)) {
      return lastPage;
    }
    return firstPage;
  }
  /**
   * Returns the history of cell edits for the current buffer.
   */
  get cellHistory() {
    return this._cellHistoryCache.get(this._bufferId) ?? [];
  }
  /**
   * Returns the most recent cell edit stored in the buffer and removes it from
   * the list of stored edits.
   */
  useLatestCellHistory() {
    this.$debugMethod("useLatestCellHistory");
    const history = this.cellHistory;
    const last = history[history.length - 1];
    if (last) {
      history.pop();
      this._cellHistoryCache.set(this._bufferId, history);
    }
    return last;
  }
  /**
   * Adds a cell edit to the history of cell edits for the buffer. If the history
   * is at its maximum size, the oldest history will be removed.
   */
  setCellHistory(changes) {
    this.$debugMethod("setCellHistory");
    const originalValues = changes.reduce((memo, change) => {
      const row = this.getRow(change.position);
      if (row) {
        const cell = row.cells[change.column];
        memo.push({ ...change, value: cell == null ? void 0 : cell.value });
      }
      return memo;
    }, []);
    if (!originalValues.length) {
      return;
    }
    const history = this.cellHistory;
    if (history.length === MAX_CELL_HISTORY) {
      history.shift();
    }
    history.push(originalValues);
    this._cellHistoryCache.set(this._bufferId, history);
  }
  /**
   * Creates an empty row and adds it to the buffer.
   *
   * @param row
   * @param label
   */
  createGhostRow(row, label) {
    var _a, _b;
    this.$debugMethod("createGhostRow");
    const key = this.rowKey(row, true);
    if (!((_a = this._columnConfig) == null ? void 0 : _a.length)) {
      this.$warn(key, "missing column config for new row");
      return;
    }
    const record = {
      id: void 0,
      position: row,
      rowIndex: row + 1,
      label: label ?? this.totalRecords + 1,
      cells: (_b = this._columnConfig) == null ? void 0 : _b.map((c, index) => ({
        value: c.defaultValue,
        column: index
      }))
    };
    this.updateBufferedRecord(row, record);
    return record;
  }
  /**
   * Locks a cell.
   *
   * @param row
   * @param col
   * @param cell
   * @param id
   */
  lockCell(row, col, cell, id) {
    this.$debugMethod("lockCell");
    const lockedCellKey = this.lockedCellKey(row, col, id);
    this._lockedCells.set(lockedCellKey, { ...cell, active: true });
    this.emitCellChange(row, col);
    this.$debug(lockedCellKey, "lock obtained on cell");
  }
  /**
   * Releases the lock on a cell.
   *
   * @param row
   * @param col
   * @param id
   */
  releaseCell(row, col, id) {
    this.$debugMethod("releaseCell");
    const lockedRow = this.getLockedRow(row, id);
    const lockedCellKey = this.lockedCellKey(row, col, id);
    this._lockedCells.delete(lockedCellKey);
    if (this._lockActiveRecords) {
      if (Array.isArray(lockedRow) && lockedRow.length) {
        this.setLockedRow(row, id, []);
        this.updateCells(lockedRow, false, true);
      } else {
        this.setLockedRow(row, id, false);
      }
    }
    this.emitCellChange(row, col);
    this.$debug(lockedCellKey, "released lock on cell");
  }
  /**
   * Releases all locks on cells and rows in the buffer. This will:
   * - Clear all locked cells from _lockedCells
   * - Clear all locked rows from _lockedRecords
   * - Emit change events for all affected cells
   * - Process any pending changes in locked rows
   */
  releaseLocks() {
    this.$debugMethod("releaseLocks");
    const affectedCells = /* @__PURE__ */ new Set();
    this._lockedCells.forEach((_, key) => {
      affectedCells.add(key);
    });
    this._lockedCells.clear();
    this._lockedRecords.clear();
    affectedCells.forEach((cellKey) => {
      const { row, col } = this.getCellCoordinatesFromKey(cellKey);
      this.emitCellChange(row, col);
    });
    this.$debug("all locks released");
  }
  /**
   * Updates the cell value in the buffer if the value has changed. Patching
   * the cell releases a lock on it.
   *
   * @param row
   * @param col
   * @param cell
   */
  patchCell(row, col, cell) {
    this.$debugMethod("patchCell");
    const key = this.cellKey(row, col, true);
    const buffer = this.buffer;
    const record = buffer.get(row);
    if (!record) {
      this.$debug(key, "ignoring merge because row is out of bounds");
      return false;
    }
    if (isEqual(record.cells[col], cell)) {
      this.$debug(key, "ignoring merge because no changes have been made");
      return false;
    }
    record.cells[col] = cell;
    buffer.set(row, record);
    this._recordsCache.set(this._bufferId, buffer);
    this.releaseCell(row, col, record.id);
    this.$debug(key, "applying merge");
    return true;
  }
  /**
   * Returns a cell stored in the buffer. If the cell is locked, it returns the
   * locked value.
   *
   * @param row
   */
  getCell(row, col) {
    var _a;
    this.$debugMethod("getCell");
    const key = this.cellKey(row, col, true);
    const record = this.buffer.get(row);
    if (!record) {
      this.$debug(key, "out of bounds - row not found");
      return void 0;
    }
    const locked = this._lockedCells.get(
      this.lockedCellKey(row, col, record.id)
    );
    if (locked) {
      this.$debug(key, "found locked - returning lock");
      return { ...locked, active: true };
    }
    const cell = (_a = record.cells) == null ? void 0 : _a[col];
    if (!cell) {
      this.$debug(key, "out of bounds - col not found");
      return void 0;
    }
    this.$debug(key, "cell found in buffer");
    return cell;
  }
  /**
   * If a row is locked, returns its list of stored row changes. Otherwise,
   * returns false.
   *
   * @param row
   * @param rowId
   */
  getLockedRow(row, rowId) {
    if (rowId === void 0)
      return;
    return this._lockedRecords.get(this.lockedRowKey(row, rowId));
  }
  /**
   * Sets the contents of a locked row.
   *
   * @param row
   * @param rowId
   * @param data
   */
  setLockedRow(row, rowId, data) {
    if (rowId === void 0)
      return;
    return this._lockedRecords.set(this.lockedRowKey(row, rowId), data ?? false);
  }
  /**
   * Returns a record stored in the buffer.
   *
   * @param row
   */
  getRow(row) {
    this.$debugMethod("getRow");
    const key = this.rowKey(row, true);
    const record = this.buffer.get(row);
    if (!record) {
      this.$debug(key, "out of bounds - row not found");
      return void 0;
    }
    this.$debug(key, "row found in buffer");
    return record;
  }
  /**
   * Returns records stored in the buffer.
   *
   * @param row
   */
  async getRows(fromIndex, untilIndex) {
    this.$debugMethod("getRows");
    if (untilIndex - fromIndex > GET_ROWS_LIMIT) {
      throw new Error("getRows limit exceeded");
    }
    const rows = [];
    for (let i = fromIndex; i <= untilIndex; i++) {
      const record = this.buffer.get(i);
      if (!record) {
        const pageNumber = this.getPageNumberForRow(i);
        await this.loadPage({ page: pageNumber, skipManagePages: true });
        i -= 1;
        continue;
      }
      rows.push(record);
    }
    this.managePages();
    return rows;
  }
  getPageNumberForRow(row) {
    return Math.floor(row / this._pageSize);
  }
  /**
   * Updates a collection of cells. If a cell belongs to a row that doesn't yet
   * exist (a ghost row), it creates that row. It stores a cell edit history if
   * the update is successful.
   *
   * @param payload
   * @param skipHistory
   */
  async updateCells(payload, skipHistory, skipFormatting, insertAfter) {
    var _a, _b, _c, _d;
    this.$debugMethod("updateCells");
    this.totalRecords;
    let patches = [];
    const changes = groupByRow(payload);
    const ghostRows = {};
    if (skipFormatting || insertAfter) {
      patches = payload;
    } else {
      patches = changes.reduce((memo, change) => {
        let shouldLockRow = false;
        change.patch.forEach((p) => {
          const row = change.position;
          const column = p.column;
          const cell = this.getCell(row, column);
          const ghost = !change.id;
          const locked = this.getLockedRow(row, change.id);
          if (ghost || cell && !isEqual(cell.value, p.value)) {
            if (ghost && !ghostRows[row]) {
              const ghostRow = this.createGhostRow(row, row + 1);
              if (ghostRow) {
                ghostRows[row] = clone(ghostRow);
              }
            }
            const c = {
              ...cell ?? {},
              status: {},
              mutated: false,
              value: p.value,
              column
            };
            const patch = {
              ...p,
              id: change.id,
              position: row
            };
            if (locked && Array.isArray(locked)) {
              locked.push(patch);
              this.setLockedRow(row, change.id, locked);
            } else {
              memo.push(patch);
              shouldLockRow = true;
            }
            this.lockCell(row, column, c, change.id);
          }
        });
        if (shouldLockRow && this._lockActiveRecords && !this.getLockedRow(change.position, change.id)) {
          this.setLockedRow(change.position, change.id, []);
        }
        return memo;
      }, []);
    }
    if (!patches.length) {
      this.$warn("no cells changed");
      return;
    }
    if (!skipHistory) {
      this.setCellHistory(patches);
    }
    const ghostRowValues = Object.values(ghostRows);
    if (insertAfter) {
      this.injectNewRecordRow(insertAfter);
    }
    try {
      const result = await ((_a = this.onDataUpdate) == null ? void 0 : _a.call(
        this,
        { changes },
        this._columnConfig,
        insertAfter
      ));
      if (result && !insertAfter) {
        patches.forEach((p) => {
          const cell = this.getCell(p.position, p.column) ?? {};
          this.patchCell(p.position, p.column, {
            ...cell,
            value: p.value,
            active: void 0
          });
        });
      }
      if (ghostRowValues.length) {
        (_b = this.onDataAddSuccess) == null ? void 0 : _b.call(this, ghostRowValues);
      }
      (_c = this.onDataUpdateSuccess) == null ? void 0 : _c.call(this);
    } catch (err) {
      this.$error("There was an error updating the cells", err);
      ghostRowValues.forEach((r) => {
        this.deleteBufferedRecord(r.position);
      });
      patches.forEach((p) => {
        this.releaseCell(p.position, p.column, p.id);
      });
      this.useLatestCellHistory();
      (_d = this.onDataUpdateError) == null ? void 0 : _d.call(this, err);
    }
  }
  /**
   * Updates a single cell. It formats the data for a single cell update to
   * pass on to the updateCells function.
   *
   * @param options
   */
  async updateCell({
    row,
    column,
    ghost,
    value,
    insertAfter
  }) {
    var _a, _b;
    this.$debugMethod("updateCell");
    const key = this.cellKey(row, column, true);
    const rowData = this.getRow(row);
    const columnKey = (_b = (_a = this._columnConfig) == null ? void 0 : _a[column]) == null ? void 0 : _b.value;
    if (!rowData && !ghost) {
      this.$warn(key, "cell cannot be updated - out of range");
      return;
    }
    const rowIndex = ghost ? row + 1 : rowData == null ? void 0 : rowData.rowIndex;
    const position = ghost ? row : rowData == null ? void 0 : rowData.position;
    const id = ghost ? void 0 : rowData == null ? void 0 : rowData.id;
    const payload = [
      {
        rowIndex,
        position,
        id,
        column,
        columnKey,
        value
      }
    ];
    this.$debug(key, "cell update requested");
    await this.updateCells(payload, void 0, void 0, insertAfter);
  }
  /**
   * Adds a collection of records to the buffer if the version is newer than
   * or equal to the current version.
   *
   * @param options
   */
  addRecords({
    records,
    page,
    version,
    bufferId,
    skipManagePages
  }) {
    this.$debugMethod("addRecords");
    const key = this.rowRangeKey(records);
    if (bufferId && bufferId !== this._bufferId) {
      return;
    }
    if (!this.setVersion(version, key)) {
      return;
    }
    if (page !== void 0) {
      this.setPageCache(page, FetchStatus.success);
      Array.from({ length: this._pageSize }, (row, index) => {
        const position = page * this._pageSize + index;
        const r = records[index];
        if (r) {
          this.updateBufferedRecord(position, r);
        } else {
          this.deleteBufferedRecord(position);
        }
      });
    } else {
      records.forEach((r) => {
        this.updateBufferedRecord(r.position, r);
      });
    }
    if (!skipManagePages) {
      this.managePages();
    }
  }
  /**
   * Loads a page of data.
   *
   * @param options
   */
  async loadPage({
    page,
    version,
    skipManagePages = false
  }) {
    var _a, _b, _c, _d, _e, _f, _g;
    const bufferId = `${this._bufferId}`;
    this.$debugMethod("loadPage");
    (_a = this.onDataLoadStart) == null ? void 0 : _a.call(this);
    this.setPageCache(page, FetchStatus.loading);
    try {
      const payload = await ((_b = this.onDataLoad) == null ? void 0 : _b.call(
        this,
        {
          pageSize: this._pageSize,
          pageNumber: page,
          ...this._params
        },
        this._columnConfig
      ));
      if (bufferId !== this._bufferId) {
        this.deletePageFromCache(page, bufferId);
        (_c = this.onDataLoadSuccess) == null ? void 0 : _c.call(this);
        return;
      }
      if (payload) {
        this.addRecords({
          records: payload,
          page,
          version,
          bufferId,
          skipManagePages
        });
      } else {
        this.setPageCache(page, FetchStatus.success);
      }
      (_d = this.onDataLoadSuccess) == null ? void 0 : _d.call(this);
      return payload;
    } catch (err) {
      if (err.revert) {
        this.setPageCache(page, FetchStatus.success);
        (_e = this.onDataLoadSuccess) == null ? void 0 : _e.call(this);
        return this.getRecordsFromBuffer(page);
      }
      this.$warn("Error fetching data", { err });
      if (bufferId !== this._bufferId) {
        this.deletePageFromCache(page, bufferId);
        (_f = this.onDataLoadError) == null ? void 0 : _f.call(this);
        return;
      }
      this.setPageCache(page, FetchStatus.failed);
      (_g = this.onDataLoadError) == null ? void 0 : _g.call(this);
      if (!skipManagePages) {
        this.managePages();
      }
    }
  }
  /**
   * Loads multiple pages in one request. It defaults to loading the current
   * pages in view unless another list of page numbers is specified.
   *
   * @param options
   */
  async loadPages(options) {
    let results;
    let pages = (options == null ? void 0 : options.pages) ?? this._pagePointers;
    if (!pages.length) {
      pages = [0];
    }
    for (let i = 0; i < pages.length; i++) {
      const rows = await this.loadPage({
        page: pages[i],
        version: options == null ? void 0 : options.version
      });
      if (rows) {
        results = [...results ?? [], ...rows];
      }
    }
    return results;
  }
  /**
   * Loads the next page of data down from the current page in view if that
   * data has not yet been loaded.
   */
  async loadNextPageIfNeeded() {
    this.$debugMethod("loadNextPageIfNeeded");
    const nextPageNumber = (this._pagePointers[0] ?? 0) + 1;
    const page = this.pageCache.get(nextPageNumber);
    if (!page && nextPageNumber <= this.lastPage) {
      return await this.loadPage({
        page: nextPageNumber,
        version: this._latestVersion
      });
    }
  }
  /**
   * Emits a row change for every row currently in view, which will cause those
   * rows to visually update if needed.
   */
  refreshCurrentPages() {
    this.$debugMethod("refreshCurrentPages");
    const page = this._pagePointers[0] ?? 0;
    const size = this._pageSize * this._pagePointers.length;
    const start = page * this._pageSize;
    Array.from({ length: size }, (row, index) => {
      const position = start + index;
      this.emitRowChange(position);
    });
  }
  /**
   * If the cache has reached its maximum size, this trims the page furthest
   * from the current page.
   */
  managePages() {
    this.$debugMethod("managePages");
    if (this.pageCache.size <= this._pageCacheLimit) {
      return;
    }
    const page = this.furthestPage;
    const startPosition = page * this._pageSize;
    const endPosition = startPosition + this._pageSize;
    for (let i = startPosition; i < endPosition; i++) {
      this.deleteBufferedRecord(i);
    }
    this.deletePageFromCache(page);
  }
  /**
   * Calculates the next page to load from the scrollTop, scrollDirection and
   * visible height.
   *
   * @param scrollTop
   * @param scrollDirection
   * @param visibleHeight
   */
  getNextPageNumberFromScrollTop(scrollTop, scrollDirection, visibleHeight) {
    this.$debugMethod("getNextPageNumberFromScrollTop");
    const overscanHeight = OVERSCAN_ROW_COUNT * this.rowHeight;
    const pageHeight = this._pageSize * this.rowHeight;
    if (scrollTop - overscanHeight <= 0)
      return 0;
    if (scrollDirection === EScrollDirection.prev) {
      return Math.ceil((scrollTop - overscanHeight) / pageHeight) - 1;
    }
    return Math.ceil((scrollTop + visibleHeight + overscanHeight) / pageHeight) - 1;
  }
  /**
   * Calculates the current pages in view from the scrollTop and visible height.
   * If rows 110 - 180 are in view, then the current page is just page 1. But if
   * rows 190 - 250 are in view, then the current pages are both page 1 and page 2.
   *
   * @param scrollTop
   * @param visibleHeight
   */
  getCurrentPages(scrollTop, visibleHeight) {
    this.$debugMethod("getCurrentPages");
    const firstRow = Math.ceil(scrollTop / this.rowHeight);
    const firstPage = Math.floor(firstRow / this._pageSize);
    const lastRow = Math.ceil((scrollTop + visibleHeight) / this.rowHeight);
    const lastPage = Math.floor(lastRow / this._pageSize);
    const pages = [];
    for (let i = firstPage; i <= lastPage; i++) {
      pages.push(i);
    }
    return pages;
  }
  /**
   * Stores the current pages in view, and loads the next pages if they have not
   * yet been loaded, and are not in the process of loading.
   *
   * @param scrollTop
   * @param scrollDirection
   * @param visibleHeight
   */
  onScrollTable(scrollTop, scrollDirection, visibleHeight) {
    this.$debugMethod("onScrollTable");
    const currentPagesInView = this.getCurrentPages(scrollTop, visibleHeight);
    const nextPageToLoad = this.getNextPageNumberFromScrollTop(
      scrollTop,
      scrollDirection,
      visibleHeight
    );
    const pagesToLoad = [];
    this._pagePointers = currentPagesInView;
    [...currentPagesInView, nextPageToLoad].forEach((p) => {
      const page = this.pageCache.get(p);
      if (!page && p <= this.lastPage && pagesToLoad.indexOf(p) < 0) {
        pagesToLoad.push(p);
      }
    });
    if (pagesToLoad.length) {
      this.loadPages({ pages: pagesToLoad });
    }
  }
}
export {
  GET_ROWS_LIMIT,
  TableDataBuffer
};
